import { useState, useEffect, useCallback } from 'react';
import { workloadsApi } from '../../../../services/workloadsApi';
import { useClient } from '../../../../contexts/ClientContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { demoModeManager } from '../../../../services/demoModeManager';
import type { WorkloadSummary, WorkloadCreate, WorkloadUpdate } from '../../../../types/workload';

export function useWorkloads() {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const [workloads, setWorkloads] = useState<WorkloadSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveClientId = user?.role === 'client' ? user.client?.id : selectedClientId;
  const isDemoMode = demoModeManager.getDemoMode().isDemoMode;

  const loadWorkloads = useCallback(async () => {
    if (!effectiveClientId && !isDemoMode) {
      setWorkloads([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await workloadsApi.getAll(effectiveClientId ?? undefined);
      setWorkloads(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load workloads');
      setWorkloads([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveClientId, isDemoMode]);

  useEffect(() => {
    loadWorkloads();
  }, [loadWorkloads]);

  const createWorkload = async (data: WorkloadCreate): Promise<WorkloadSummary> => {
    const result = await workloadsApi.create(data, effectiveClientId ?? undefined);
    await loadWorkloads();
    return result;
  };

  const updateWorkload = async (id: string, data: WorkloadUpdate): Promise<WorkloadSummary> => {
    const result = await workloadsApi.update(id, data);
    await loadWorkloads();
    return result;
  };

  const deleteWorkload = async (id: string): Promise<void> => {
    await workloadsApi.delete(id);
    await loadWorkloads();
  };

  const linkProject = async (workloadId: string, projectId: string): Promise<void> => {
    await workloadsApi.linkProject(workloadId, projectId);
    await loadWorkloads();
  };

  const unlinkProject = async (workloadId: string, projectId: string): Promise<void> => {
    await workloadsApi.unlinkProject(workloadId, projectId);
    await loadWorkloads();
  };

  return {
    workloads,
    isLoading,
    error,
    refresh: loadWorkloads,
    createWorkload,
    updateWorkload,
    deleteWorkload,
    linkProject,
    unlinkProject,
  };
}
